/**
 * What a file dropped onto Flashka IS, so every drop target (web Home, the
 * deck page, Ka chat, the new-deck modal) routes it the same way. Pure — each
 * client turns its own drop event into `DroppedFileInfo`s.
 */
import { isApkgFileName } from '../apkg-import/extract';
import { audioUploadProblem, isLikelyAudioFile } from '../transcription/audio-file';
import { DOCUMENT_MIME_TYPES } from './document-limits';

/** Matches the `AddMaterialType` names for the first three. */
export type DropKind = 'document' | 'photos' | 'audio' | 'anki';

export type DroppedFileInfo = {
  name: string;
  type: string;
  /** A dropped folder — or a macOS `.pages`/`.key` package, which is one. */
  isDirectory?: boolean;
  /** Bytes — which recording of a drop makes a new deck (`splitDrop`). */
  size?: number;
};

const PHOTO_MIME = /^image\/(png|jpe?g|gif|webp)$/i;
const PHOTO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'pptx', 'ppt', 'txt', 'pages', 'key'];

function extensionOf(name: string): string {
  const base = name.split(/[/\\]/).pop() ?? '';
  return base.includes('.') ? base.split('.').pop()!.toLowerCase() : '';
}

/** `null` = nothing in Flashka takes this file (video, zip, csv, heic, svg…). */
export function dropKindOf(file: DroppedFileInfo): DropKind | null {
  if (file.isDirectory) return null;
  if (isApkgFileName(file.name)) return 'anki';
  const type = file.type.toLowerCase();
  const ext = extensionOf(file.name);
  // Browsers report a `.webm` recording (the recorder's own format) as video.
  if (ext === 'webm') return 'audio';
  if (type.startsWith('video/')) return null;
  if (PHOTO_MIME.test(type) || (!type && PHOTO_EXTENSIONS.includes(ext))) return 'photos';
  if (isLikelyAudioFile(type, file.name)) return 'audio';
  if (type === 'application/zip')
    return ext === 'pages' || ext === 'key' ? 'document' : null;
  if (
    (DOCUMENT_MIME_TYPES as readonly string[]).includes(type) ||
    DOCUMENT_EXTENSIONS.includes(ext)
  ) {
    return 'document';
  }
  return null;
}

/** Which kind of a mixed drop a NEW deck is made from — the highest present. */
export const DROP_KIND_PRECEDENCE: readonly DropKind[] = [
  'document',
  'photos',
  'audio',
  'anki',
];

/** Kinds whose flows take exactly one file per run. */
const SINGLE_FILE_KINDS: ReadonlySet<DropKind> = new Set(['audio', 'anki']);

/** Why a target left a dropped file out — the line of the toast that names it. */
export type DropSkipReason =
  | 'folder'
  | 'video'
  | 'unsupported'
  /** A file Flashka takes, but not here (an `.apkg` on a deck). */
  | 'notHere'
  /** An `.apkg` beside other files, or a second one: it imports on its own. */
  | 'alone';

export function dropSkipReason(
  file: DroppedFileInfo,
  accept: readonly DropKind[],
): DropSkipReason {
  if (file.isDirectory) return 'folder';
  const kind = dropKindOf(file);
  if (!kind)
    return file.type.toLowerCase().startsWith('video/') ? 'video' : 'unsupported';
  return accept.includes(kind) ? 'alone' : 'notHere';
}

/** A drop's skipped files by reason, each reason once, in drop order. */
export function groupDropSkips<T extends DroppedFileInfo>(
  files: readonly T[],
  accept: readonly DropKind[],
): { reason: DropSkipReason; names: string[] }[] {
  const groups = new Map<DropSkipReason, string[]>();
  for (const file of files) {
    const reason = dropSkipReason(file, accept);
    groups.set(reason, [...(groups.get(reason) ?? []), file.name]);
  }
  return [...groups].map(([reason, names]) => ({ reason, names }));
}

/**
 * A file name short enough for a toast, cut in the MIDDLE so its extension —
 * often the reason it was refused — stays readable: `dNVGbJaUQr…RpW9JLg.mp4`.
 */
export function shortFileName(name: string, max = 32): string {
  // Code points, not UTF-16 units: a cut must never split an emoji in half.
  if (Array.from(name).length <= max) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 && name.length - dot <= 8 ? name.slice(dot) : '';
  const stem = Array.from(name.slice(0, name.length - ext.length));
  const keep = max - ext.length - 1;
  const head = Math.ceil(keep * 0.6);
  const tail = stem.slice(stem.length - (keep - head));
  return `${stem.slice(0, head).join('')}…${tail.join('')}${ext}`;
}

/** Files a deck is not made from that join it once it exists, as on an existing deck. */
export type DropJoins<T> = {
  /** One "Photos" tile. */
  photos: T[];
  /** Each its own tile and transcription credit. */
  recordings: T[];
};

export type DropSplit<T> = {
  kind: DropKind;
  files: T[];
  joins: DropJoins<T>;
  skipped: T[];
};

/**
 * Which files of a drop that makes a NEW deck (Home, a folder, a group, the
 * create modal) the target takes. `null` = none of them (the caller leaves
 * the drop unclaimed). The highest kind makes the deck; every OTHER photo and
 * recording the target takes joins it once it exists (owner, 2026-10-21).
 * `skipped` are the rest, for one toast.
 */
export function splitDrop<T extends DroppedFileInfo>(
  files: readonly T[],
  accept: readonly DropKind[],
): DropSplit<T> | null {
  const kinds = files.map(dropKindOf);
  const kind = DROP_KIND_PRECEDENCE.find((k) => accept.includes(k) && kinds.includes(k));
  if (!kind) return null;
  let taken = files.filter((_, i) => kinds[i] === kind);
  if (SINGLE_FILE_KINDS.has(kind)) {
    // A recording the Audio form refuses would sink the deck and every
    // recording joining it: make it from the first one the form takes.
    const usable =
      kind === 'audio' ? taken.find((f) => !audioUploadProblem(f)) : undefined;
    taken = [usable ?? taken[0]!];
  }
  const joining = (k: DropKind) =>
    accept.includes(k)
      ? files.filter((f, i) => kinds[i] === k && !taken.includes(f))
      : [];
  const joins = { photos: joining('photos'), recordings: joining('audio') };
  const joined = [...joins.photos, ...joins.recordings];
  return {
    kind,
    files: taken,
    joins,
    skipped: files.filter((f) => !taken.includes(f) && !joined.includes(f)),
  };
}

export type DropGroups<T> = {
  /** One per kind present, in {@link DROP_KIND_PRECEDENCE} order. */
  groups: { kind: DropKind; files: T[] }[];
  skipped: T[];
};

/**
 * A drop on an EXISTING deck: every kind the target takes, every file of
 * it — several recordings are several recordings (owner, 2026-10-20).
 * `null` = none of them.
 */
export function groupDrop<T extends DroppedFileInfo>(
  files: readonly T[],
  accept: readonly DropKind[],
): DropGroups<T> | null {
  const kinds = files.map(dropKindOf);
  const groups = DROP_KIND_PRECEDENCE.filter((k) => accept.includes(k))
    .map((kind) => ({ kind, files: files.filter((_, i) => kinds[i] === kind) }))
    .filter((g) => g.files.length > 0);
  if (!groups.length) return null;
  return {
    groups,
    skipped: files.filter((_, i) => !groups.some((g) => g.kind === kinds[i])),
  };
}

/**
 * In-app image drags (a note image, a PDF figure, the image-occlusion
 * selection) carry their image under these types. The strings are the note
 * image's originals — every existing drop zone already understands them.
 * The payload is a `data:` or `https:` URL.
 */
export const IN_APP_IMAGE_DRAG_MIME = 'application/x-flashka-note-image';
export const IN_APP_IMAGE_FILEPATH_MIME = 'application/x-flashka-note-image-filepath';

export function isInAppImageDrag(types: ArrayLike<string> | null | undefined): boolean {
  return !!types && Array.from(types).includes(IN_APP_IMAGE_DRAG_MIME);
}

/** Where an in-app image is being dropped — decides accept vs refuse. */
export type InAppImageDropTarget =
  | { kind: 'chat'; attached: number; max: number }
  | { kind: 'card-image'; readOnly: boolean; occlusion: boolean };

export function decideInAppImageDrop(target: InAppImageDropTarget): 'accept' | 'invalid' {
  if (target.kind === 'chat') return target.attached < target.max ? 'accept' : 'invalid';
  return target.readOnly || target.occlusion ? 'invalid' : 'accept';
}
